import { Box, Button, DrawerCloseButton, Flex, Text } from '@chakra-ui/react';
import { InDesktop } from '@ui/components/MobileOrDesktop';
import {
  ModalOrDragDrawerContent,
  ModalOrDrawer,
  ModalOrDrawerBodyWithMask,
  ModalOrDrawerFooter,
  ModalOrDrawerHeader,
  ModalOrDrawerOverlay,
} from '@ui/components/ModalOrDrawer/ModalOrDrawerParts';
import { COIN_MODE, useCoinMode } from '@ui/hooks/useCoinMode';
import { useTranslations } from '@ui/i18n';
import { unstakePeriodDict } from '@ui/utils/unstake/periodDict';

type UnstakeLearnMoreDrawerProps = {
  open: boolean;
  close: () => void;
};

const outerShadow = 'inset -2px -2px 3px #FFFFFF, inset 2px 2px 3px #B4C1D5';
const rectangleShadow = '-1px -1px 1px #F0F0F0, 1px 1px 1px #B4C1D5';

const UnstakeLearnMoreContent = () => {
  const t = useTranslations();
  const globalCoinMode = useCoinMode();
  const coinModeInfo = COIN_MODE[globalCoinMode];
  const periodInfo = unstakePeriodDict[globalCoinMode];

  return (
    <>
      <Flex mb={2} justify="space-between" textStyle="captionBold">
        <Text color="red">
          {t('{many} days', {
            many: `1 - ${periodInfo.batchDays}`,
          })}
        </Text>
        <Text color="text.inactive">
          {t('{many} days', {
            many: periodInfo.unbondingDays,
          })}
        </Text>
        <Text color="green">{t('Claimable')}</Text>
      </Flex>
      <Flex mb="32px" gap="2.5px">
        <Flex
          w="57px"
          h="18px"
          shadow={outerShadow}
          rounded="13px"
          justify="center"
          align="center"
        >
          <Box
            background="linear-gradient(271.98deg, #C50F0F 5.14%, #FF5959 95.91%)"
            rounded="13px"
            w="47px"
            h="10px"
            shadow={rectangleShadow}
          />
        </Flex>
        <Flex
          flex="1"
          h="18px"
          shadow={outerShadow}
          justify="center"
          align="center"
          rounded="13px"
          px="5px"
        >
          <Box
            background="linear-gradient(270.11deg, #6F609D 1.17%, #9486BD 98.81%)"
            rounded="13px"
            w="full"
            h="10px"
            shadow={rectangleShadow}
          />
        </Flex>
        <Flex
          w="57px"
          h="18px"
          shadow={outerShadow}
          rounded="13px"
          justify="center"
          align="center"
        >
          <Box
            background="linear-gradient(272.38deg, #01B08F 3.06%, #1FD6B3 95.32%)"
            rounded="13px"
            w="47px"
            h="10px"
            shadow={rectangleShadow}
          />
        </Flex>
      </Flex>
      <Flex flexDir="column" gap="24px">
        <Flex flexDir="column" gap="8px">
          <Text textStyle="h4">
            {t('{many} days', {
              many: `Batching 1 - ${periodInfo.batchDays}`,
            })}
          </Text>
          <Text textStyle="body">
            {t('unstake-learn-more-1-{liquidCoin}-{batchDays}-{lastDay}', {
              liquidCoin: coinModeInfo.liquidName,
              batchDays: periodInfo.batchDays,
              lastDay: periodInfo.batchDays - 1,
            })}
          </Text>
        </Flex>
        <Flex flexDir="column" gap="8px">
          <Text textStyle="h4">
            {t('{many} days', {
              many: `Unbonding ${periodInfo.unbondingDays}`,
            })}
          </Text>
          <Text textStyle="body">
            {t('unstake-learn-more-2-{coin}-{unbondingDays}', {
              coin: coinModeInfo.name,
              unbondingDays: periodInfo.unbondingDays,
            })}
          </Text>
        </Flex>
        <Flex flexDir="column" gap="8px">
          <Text textStyle="h4">{t('Claimable')}</Text>
          <Text textStyle="body">
            {t('unstake-learn-more-3-{liquidCoin}', {
              liquidCoin: coinModeInfo.liquidName,
            })}
          </Text>
        </Flex>
      </Flex>
    </>
  );
};

const UnstakeLearnMoreDrawer = ({
  open,
  close,
}: UnstakeLearnMoreDrawerProps) => {
  const t = useTranslations();

  return (
    <ModalOrDrawer
      isOpen={open}
      onClose={close}
      scrollBehavior="inside"
      placement="bottom"
      size={{
        base: 'full',
        desktop: 'md',
      }}
    >
      <ModalOrDrawerOverlay />
      <ModalOrDragDrawerContent>
        <ModalOrDrawerHeader>{t('Learn more')}</ModalOrDrawerHeader>
        <InDesktop>
          <DrawerCloseButton />
        </InDesktop>
        <ModalOrDrawerBodyWithMask>
          <Text textAlign="center" mb="40px" textStyle="h3">
            {t('Unstaking process')}
          </Text>
          <Box textStyle="body">
            <UnstakeLearnMoreContent />
          </Box>
        </ModalOrDrawerBodyWithMask>
        <ModalOrDrawerFooter>
          <Button mt="auto" onClick={close}>
            {t('Close')}
          </Button>
        </ModalOrDrawerFooter>
      </ModalOrDragDrawerContent>
    </ModalOrDrawer>
  );
};

export default UnstakeLearnMoreDrawer;
